import type { Pod, Container, Service } from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

const NODES = ["ip-10-42-11-14.ec2.internal", "ip-10-42-11-31.ec2.internal", "ip-10-42-12-8.ec2.internal", "ip-10-42-13-2.ec2.internal", "ip-10-42-13-40.ec2.internal"];

function mkContainer(rng: () => number, name: string, s: Service): Container {
  return {
    name,
    image: `ghcr.io/coremonit/${s.name}:${s.version}`,
    status: s.status === "critical" && rng() < 0.4 ? "waiting" : "running",
    restartCount: s.status === "critical" ? int(rng, 2, 8) : int(rng, 0, 2),
    cpuMillicoresLive: int(rng, 90, 780),
    memoryMbLive: int(rng, 120, 780),
    diskMbLive: int(rng, 30, 240),
    networkKbpsLive: int(rng, 400, 8200),
    openFds: int(rng, 20, 320),
    threads: int(rng, 8, 120),
    gcPct: +(rng() * 8).toFixed(2),
    heapMb: int(rng, 64, 640),
    nativeMb: int(rng, 40, 160),
  };
}

export function generatePods(services: Service[], seed = 42): Pod[] {
  const rng = makeRng(seed);
  const out: Pod[] = [];
  for (const s of services) {
    for (let i = 0; i < s.replicas.desired; i++) {
      const suffix = Array.from({ length: 5 }, () => "abcdefghijklmnop"[Math.floor(rng() * 16)]).join("");
      const status: Pod["status"] =
        i >= s.replicas.available && s.status !== "healthy" ? "crashloop" :
        s.status === "critical" && rng() < 0.15 ? "pending" :
        "running";
      out.push({
        id: `pod_${s.id}_${i}`,
        name: `${s.name}-${suffix}-${Math.floor(rng() * 90 + 10)}`,
        serviceId: s.id,
        namespace: s.deployment.namespace ?? "default",
        node: pick(rng, NODES),
        status,
        ready: `${status === "running" ? 1 : 0}/1`,
        restartCount: status === "crashloop" ? int(rng, 3, 12) : int(rng, 0, 2),
        image: `ghcr.io/coremonit/${s.name}:${s.version}`,
        imageDigest: `sha256:${Array.from({ length: 12 }, () => "0123456789abcdef"[Math.floor(rng() * 16)]).join("")}`,
        startedAt: new Date(Date.now() - int(rng, 60, 8 * 3600) * 1000).toISOString(),
        uptimeSec: int(rng, 60, 24 * 3600),
        containers: [mkContainer(rng, s.name, s)],
        initContainers: rng() < 0.4 ? [mkContainer(rng, "wait-for-db", s)] : [],
        sidecars: [
          ...(rng() < 0.7 ? [mkContainer(rng, "istio-proxy", s)] : []),
          ...(rng() < 0.3 ? [mkContainer(rng, "fluent-bit", s)] : []),
        ],
        volumes: [
          { name: "config",  mountPath: "/etc/config",  type: "configMap", source: `${s.name}-config` },
          { name: "secrets", mountPath: "/etc/secrets", type: "secret",    source: `${s.name}-secret` },
          { name: "cache",   mountPath: "/var/cache",   type: "emptyDir",  source: "-" },
        ],
        env: [
          { key: "NODE_ENV", value: s.environment },
          { key: "LOG_LEVEL", value: "info" },
          { key: "OTEL_EXPORTER_OTLP_ENDPOINT", value: "http://otel-collector.observability:4318" },
          { key: "DB_URL", fromSecret: `${s.name}-secret`, masked: true },
          { key: "STRIPE_KEY", fromSecret: `${s.name}-secret`, masked: true },
        ],
        secrets: [
          { name: `${s.name}-secret`, key: "DB_URL",     masked: true },
          { name: `${s.name}-secret`, key: "STRIPE_KEY", masked: true },
          { name: "coremonit-mtls",   key: "tls.key",    masked: true },
          { name: "coremonit-mtls",   key: "tls.crt",    masked: true },
        ],
        configMaps: [`${s.name}-config`, "istio-ca-root-cert"],
      });
    }
  }
  return out;
}
